import { useMemo } from 'react'

import type { ActivityHourCell } from '@/models/IActivity'
import {
  buildHourGrid,
  formatHourRange,
  WEEKDAY_LABELS
} from '@/utils/activity'

type ActivityHourGridProps = {
  cells: ActivityHourCell[]
}

/** 7 x 24 grid of average active users (weekday rows, hour columns, Europe/Berlin) */
export const ActivityHourGrid = ({ cells }: ActivityHourGridProps) => {
  const grid = useMemo(() => buildHourGrid(cells), [cells])

  return (
    <div className="overflow-x-auto text-foreground">
      <div
        aria-label="Average active users by weekday and hour"
        className="grid min-w-[640px] gap-[3px]"
        role="img"
        style={{ gridTemplateColumns: '32px repeat(24, minmax(0, 1fr))' }}
      >
        <span />
        {Array.from({ length: 24 }, (_, hour) => (
          <span
            className="text-[10px] tabular-nums text-muted-foreground/60"
            key={`h-${hour}`}
          >
            {hour % 3 === 0 ? hour : ''}
          </span>
        ))}
        {grid.rows.map((row, r) => (
          <HourRow
            key={WEEKDAY_LABELS[r]}
            label={WEEKDAY_LABELS[r]}
            max={grid.max}
            row={row}
          />
        ))}
      </div>
    </div>
  )
}

const HourRow = ({
  label,
  row,
  max
}: {
  label: string
  row: number[]
  max: number
}) => (
  <>
    <span className="self-center text-[10px] text-muted-foreground/60">
      {label}
    </span>
    {row.map((value, hour) => {
      const intensity = max > 0 ? value / max : 0

      return (
        <span
          className="h-5 rounded-[3px] bg-muted/40"
          key={hour}
          style={
            value > 0
              ? {
                  backgroundColor: `hsl(var(--secondary) / ${(
                    0.15 +
                    0.85 * intensity
                  ).toFixed(2)})`
                }
              : undefined
          }
          title={`${label} ${formatHourRange(hour)}: ø ${value.toFixed(
            1
          )} active`}
        />
      )
    })}
  </>
)

export const formatHourPeak = (
  cells: ActivityHourCell[]
): string | undefined => {
  const { peak } = buildHourGrid(cells)

  return peak
    ? `Peak: ${WEEKDAY_LABELS[peak.weekday - 1]} ${formatHourRange(
        peak.hour
      )} · ø ${peak.value.toFixed(1)} active`
    : undefined
}
